import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import {
  MdMailOutline,
  MdDrafts,
  MdClose,
  MdPrint,
  MdDownload,
  MdArrowBack,
  MdWarning,
  MdInfo,
  MdCheckCircle,
  MdError,
} from "react-icons/md";
import { AuthContext } from "../context/AuthContext";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const API_URL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_API_URL_DEVELOPMENT
    : process.env.REACT_APP_API_URL;

// Map letter types to icons and colors
const letterTypeConfig = {
  termination: { icon: <MdError />, color: '#d32f2f', label: 'Termination' },
  suspension: { icon: <MdWarning />, color: '#f57c00', label: 'Suspension' },
  warning: { icon: <MdWarning />, color: '#fbc02d', label: 'Warning' },
  performance: { icon: <MdInfo />, color: '#1976d2', label: 'Performance' },
  policy: { icon: <MdInfo />, color: '#388e3c', label: 'Policy' },
  general: { icon: <MdMailOutline />, color: '#757575', label: 'General' },
};

const MyLetters = () => {
  const { userRole } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch letters on mount
  useEffect(() => {
    fetchLetters();
  }, []);

  const fetchLetters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/letters/my-letters`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch letters");
      }

      const data = await response.json();
      setLetters(data);
      
      // Count unread letters
      const unread = data.filter(letter => !letter.isRead).length;
      setUnreadCount(unread);
      
    } catch (err) {
      console.error("Error fetching letters:", err);
      setError("Failed to load letters");
    } finally {
      setLoading(false);
    }
  };

  const handleViewLetter = async (letter) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/letters/my-letters/${letter.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch letter details");
      }

      const data = await response.json();
      setSelectedLetter(data);
      setModalOpen(true);
      
      // Update local state to mark as read
      setLetters(prevLetters => 
        prevLetters.map(l => 
          l.id === letter.id ? { ...l, isRead: true } : l
        )
      );
      
      // Update unread count
      if (!letter.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
    } catch (err) {
      console.error("Error fetching letter:", err);
      setError("Failed to load letter details");
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLetter(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const getLetterConfig = (letterType) => {
    const type = letterType?.toLowerCase();
    return letterTypeConfig[type] || letterTypeConfig.general;
  };

  const renderLetterList = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      );
    }

    if (letters.length === 0) {
      return (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: "rgba(0, 0, 0, 0.02)",
            borderRadius: 2,
          }}
        >
          <MdMailOutline size={48} color="#ccc" />
          <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
            No letters found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You haven't received any letters yet.
          </Typography>
        </Paper>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {letters.map((letter, index) => {
          const config = getLetterConfig(letter.letterType);
          return (
            <React.Fragment key={letter.id}>
              <ListItemButton
                onClick={() => handleViewLetter(letter)}
                sx={{
                  py: 3,
                  px: { xs: 2, md: 3 },
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.04)",
                  },
                  bgcolor: !letter.isRead ? "rgba(39, 106, 120, 0.05)" : "transparent",
                }}
              >
                <ListItemIcon>
                  <Badge
                    color="primary"
                    variant="dot"
                    invisible={letter.isRead}
                    sx={{
                      "& .MuiBadge-dot": {
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: `${config.color}20`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: config.color,
                      }}
                    >
                      {config.icon}
                    </Box>
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: letter.isRead ? 400 : 600,
                          flex: 1,
                        }}
                      >
                        {letter.letterName}
                      </Typography>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          bgcolor: `${config.color}20`,
                          color: config.color,
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {letter.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sent {dayjs(letter.sentAt).fromNow()} by {letter.sentByName}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
              {index < letters.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
          maxWidth: 1200,
          mx: "auto",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: "#276a78",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <MdMailOutline />
            My Letters
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} unread`}
                color="primary"
                size="small"
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            View all letters and communications sent to you
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {renderLetterList()}
      </Paper>

      {/* Letter Detail Dialog */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: isMobile ? "100vh" : "auto",
            maxHeight: isMobile ? "100vh" : "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#276a78",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedLetter?.letterName}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            sx={{ color: "white" }}
          >
            <MdClose />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedLetter && (
            <Box>
              <Box
                sx={{
                  p: 3,
                  bgcolor: "rgba(39, 106, 120, 0.05)",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Date Sent
                    </Typography>
                    <Typography variant="body1">
                      {dayjs(selectedLetter.sentAt).format("MMMM D, YYYY h:mm A")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Sent By
                    </Typography>
                    <Typography variant="body1">
                      {selectedLetter.sentByName} ({selectedLetter.sentByEmail})
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Subject
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedLetter.subject}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  p: 3,
                  "& pre": {
                    whiteSpace: "pre-wrap",
                    fontFamily: "inherit",
                    margin: 0,
                  },
                }}
              >
                <pre>{selectedLetter.content}</pre>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Button
            onClick={handlePrint}
            startIcon={<MdPrint />}
            sx={{ mr: "auto" }}
          >
            Print
          </Button>
          <Button onClick={handleCloseModal} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyLetters;